import { fromJS, List, Map } from "immutable";

import clipboardCatcherClassName from "../constants/clipboardCatcherClassName";
import { ConstraintTypes } from "../constants/constraints";
import interfaceMessages from "../constants/interfaceMessages";
import pasteDataTypes from "../constants/pasteDataTypes";
import { REFERENCE_RELATES_TO } from "../constants/references";
import dialogs from "../dialogs";
import { appStateOperations } from "../modules/appState";
import batchOperations from "../modules/batchOperations";
import { dateConstraintOperations } from "../modules/dateConstraints";
import { dependencyOperations } from "../modules/dependencies";
import { itemOperations,itemSelectors } from "../modules/items";
import { relationshipOperations } from "../modules/relationships";
import { tagOperations } from "../modules/tags";
import { ItemPasteResultRecord } from "../types/immutableRecords/ItemPasteResultRecord";
import { TimespanRecord } from "../types/immutableRecords/model/data/TimespanRecord";
import isEditable from "./isEditable";
import { getLabelWithUntitled } from "./labelUtils";

const mapIdToValue = (rm, key, byIdMap) => {
  return rm.update(key, ids =>
    ids ? (List.isList(ids) ? ids.map(id => byIdMap.get(id) || id) : byIdMap.get(ids) || ids) : ids
  );
};

const getCopyItemsJsonString = (
  itemsById,
  attachmentsById,
  dependenciesById,
  dateConstraintsById,
  relationshipsById,
  propertiesById,
  typesById,
  colorsById,
  tagsById,
  selectedItemIds,
  itemSequence,
  projectId
) => {
  let itemsRecords = selectedItemIds.map(sid => {
    // Flat the id referring values
    const processedItem = itemsById.get(sid).withMutations(rm => {
      mapIdToValue(rm, "attachments", attachmentsById);
      mapIdToValue(rm, "image", attachmentsById);
      mapIdToValue(rm, "links", attachmentsById);
      mapIdToValue(rm, "blockedByDependencies", dependenciesById);
      mapIdToValue(rm, "blockingDependencies", dependenciesById);
      mapIdToValue(rm, "dateConstraints", dateConstraintsById);
      mapIdToValue(rm, "inverseReferences", relationshipsById);
      mapIdToValue(rm, "references", relationshipsById);
      mapIdToValue(rm, "color", colorsById);
      mapIdToValue(rm, "tags", tagsById);
      // Copy the swatch color value
      rm.update("color", c => c.swatchColor || c);

      // Remove children which will be updated in the following process
      rm.set("children", List());

      // Find the closest super in the copying items
      const superId = itemSelectors.getClosestValidAncestor(rm.get("id"), null, itemsById, typesById, selectedItemIds);
      if (superId && selectedItemIds.find(id => id === superId)) {
        rm.set("super", superId);
      }
    });
    return processedItem;
  });

  // Update item children
  itemsRecords.forEach(item => {
    const superId = item.get("super");
    if (superId) {
      const foundIndex = itemsRecords.findIndex(i => i.get("id") === superId);
      if (foundIndex >= 0) {
        itemsRecords = itemsRecords.updateIn([foundIndex, "children"], c => c.push(item.get("id")));
      }
    }
  });

  // Find the properties existed in the items
  const properties = itemsRecords
    .reduce((props, item) => {
      const itemProps = item.propertyValues
        .filter((v, k) => !props.has(k))
        .map((v, k) => {
          const propRecord = propertiesById.get(k);
          return Map({
            id: propRecord.id,
            format: propRecord.format
          });
        });
      return props.merge(itemProps);
    }, Map())
    .toJS();
  // Find the types of the items
  const types = itemsRecords
    .reduce((typesMap, item) => {
      const typeId = item.type;
      if (!typesMap.has(typeId)) {
        const typeRecord = typesById.get(typeId);
        return typesMap.set(
          typeId,
          Map({
            canHaveDate: typeRecord.canHaveDate,
            canIncludeInNarrative: typeRecord.canIncludeInNarrative,
            id: typeId
          })
        );
      }
      return typesMap;
    }, Map())
    .toJS();

  // Add extra attributes
  // sequence index
  const finalItems = itemsRecords.map(ir => Map(ir).set("sequenceIndex", itemSequence.indexOf(ir.get("id"))));

  const resultObj = {
    projectId,
    items: finalItems.toJS(),
    properties,
    types
  };

  return JSON.stringify(resultObj);
};

const getCopyItemsPlainText = (itemsById, selectedItemIds) => {
  return selectedItemIds.map(sid => itemsById.get(sid).label).join("\n");
};

const canPaste = isActiveView => {
  return (
    isActiveView &&
    (!document.activeElement ||
      !isEditable(document.activeElement) ||
      document.activeElement.classList.contains(clipboardCatcherClassName))
  );
};

const mapObjValueToIds = (rm, key) =>
  rm.update(key, values => (values ? (List.isList(values) ? values.map(v => v.get("id")) : values.get("id")) : values));

const isCopiedDataFormSameProject = (copiedProjectId, pastingProjectId) => {
  return copiedProjectId === pastingProjectId;
};

/**
 * Fix the relationships in single project(Dependencies, Date constraints, References)
 * @param {[Immutable.Map]} origItems
 * @param {List} itemsMaps
 */
const processRelsOfPastingItemsInProject = (origItems, itemsMaps, itemsById) => {
  let relationshipActions = [];
  let processedItemsMaps = itemsMaps;
  let mappedOrigItems = fromJS(origItems);

  const processRelationship = (thisItem, thisItemIndex, thisKey, otherKey, isThisSubject, createActionFunc) => {
    // const thisSide = isThisSubject ? "subject" : "object";
    const otherSide = isThisSubject ? "object" : "subject";
    const thisRelationships = thisItem.get(thisKey);
    thisRelationships.forEach(thisRel => {
      // Check if the other side is in the pasting items
      let otherSideItemIndex = -1;
      const foundOtherSideItem = mappedOrigItems.find((otherI, otherIndex) => {
        otherSideItemIndex = otherIndex;
        const otherId = otherI.get("id");
        return otherId !== thisItem.get("id") && otherId === thisRel.get(otherSide);
      });

      // Create new relationship
      const thisProcessedId = processedItemsMaps.get(thisItemIndex).get("id");
      // If the other side item is in the pasting items, use its id
      // otherwise use the id from the relationship
      const otherProcessedId = foundOtherSideItem
        ? processedItemsMaps.get(otherSideItemIndex).get("id")
        : thisRel.get(otherSide);

      // If the other side item is in the pasting items
      // or it is existed in the target project (might be delete before pasting)
      if (foundOtherSideItem || itemsById.get(otherProcessedId)) {
        const newSubId = isThisSubject ? thisProcessedId : otherProcessedId;
        const newObjId = isThisSubject ? otherProcessedId : thisProcessedId;

        // Create new relationship
        const newDepAction = createActionFunc(newSubId, newObjId, thisRel);
        // Push action to actions array
        if (newDepAction) {
          relationshipActions.push(newDepAction);
        }
      }

      // Important: remove the value in the original items in case double processing
      // Remove the value of this item
      mappedOrigItems = mappedOrigItems.updateIn([thisItemIndex, thisKey], rels =>
        rels.filter(rel => rel.get("id") !== thisRel.get("id"))
      );

      // Remove the value of other item
      if (foundOtherSideItem) {
        mappedOrigItems = mappedOrigItems.updateIn([otherSideItemIndex, otherKey], rels =>
          rels.filter(rel => rel.get("id") !== thisRel.get("id"))
        );
      }
    });
  };

  /**
   * A shortcut of calling the processRelationship on the dual relationships attributes
   */
  const processPairRelationships = (origI, oiIndex, thisKey, otherKey, isThisSubject, createActionFunc) => {
    processRelationship(origI, oiIndex, thisKey, otherKey, isThisSubject, createActionFunc);
    processRelationship(origI, oiIndex, otherKey, thisKey, !isThisSubject, createActionFunc);
  };

  mappedOrigItems.forEach((origI, oiIndex) => {
    // Tricky: the items in mappedOrigItems may be changed during processing
    // get the latest item from the mappedOrigItems
    const thisItem = mappedOrigItems.get(oiIndex);

    // Dependencies
    processPairRelationships(
      thisItem,
      oiIndex,
      "blockingDependencies",
      "blockedByDependencies",
      true,
      (subId, objId, oldRel) => {
        return dependencyOperations.createDependencies([subId], [objId], oldRel.get("dependencyType"), [subId, objId]);
      }
    );
    // Relationships
    processPairRelationships(thisItem, oiIndex, "references", "inverseReferences", true, (subId, objId, oldRel) => {
      return relationshipOperations.createRelationship([subId], oldRel.get("reference"), [objId]);
    });
    // Date constraints
    processRelationship(thisItem, oiIndex, "dateConstraints", "dateConstraints", true, (subId, objId, oldRel) => {
      const constraintType = oldRel.get("constraintType");
      if (constraintType === ConstraintTypes.Relative) {
        return dateConstraintOperations.createRelativeConstraints(
          [subId],
          oldRel.get("subjectTarget"),
          objId,
          oldRel.get("objectTarget"),
          oldRel.get("comparison"),
          TimespanRecord(oldRel.get("offset"))
        );
      } else if (constraintType === ConstraintTypes.Date) {
        return dateConstraintOperations.createDateConstraints(
          [subId],
          oldRel.get("subjectTarget"),
          oldRel.get("comparison"),
          oldRel.get("date")
        );
      } else if (constraintType === ConstraintTypes.DatePart) {
        return dateConstraintOperations.createDatePartConstraints(
          [subId],
          oldRel.get("subjectTarget"),
          oldRel.get("partPrecision"),
          oldRel.get("partValue")
        );
      }
    });
  });
  return { relationshipActions, processedItemsMaps };
};

const deleteUnnecessaryProps = rm => {
  rm.delete("id");
  rm.delete("displayId");
  rm.delete("blockedByDependencies");
  rm.delete("blockingDependencies");
  rm.delete("references");
  rm.delete("inverseReferences");
  rm.delete("dateConstraints");
  rm.delete("tags");
};

const mapAllObjectValuesToIds = rm => {
  mapObjValueToIds(rm, "attachments");
  mapObjValueToIds(rm, "image");
  mapObjValueToIds(rm, "links");
  // The relationship kind properties will be deleted and rebuilt
  // in other steps
  // mapObjValueToIds(rm, "blockedByDependencies");
  // mapObjValueToIds(rm, "blockingDependencies");
  // mapObjValueToIds(rm, "dateConstraints");
  // mapObjValueToIds(rm, "inverseReferences");
  // mapObjValueToIds(rm, "references");
};

const convertColor = (rm, colorsById) => {
  const foundColor = colorsById.find(c => c.swatchColor === rm.get("color"));
  rm.update("color", c => (foundColor ? foundColor.id : c));
};

const getPasteTagsActions = (tags, newItemId) => tagOperations.addTags([newItemId], tags);

const processPastingInProject = (copiedDataMap, canViewPasteItemFunc, colorsById, itemsById) => {
  let actions = [];
  // Filter out the items can NOT paste in the view
  const filteredItems = canViewPasteItemFunc
    ? copiedDataMap.get("items").filter(i => canViewPasteItemFunc(i))
    : copiedDataMap.get("items");
  // Process attributes
  const itemsMapsList = filteredItems.map(i => {
    return i.withMutations(rm => {
      // Convert the flatten value back to Id
      mapAllObjectValuesToIds(rm);
      // Convert the color
      convertColor(rm, colorsById);
      // Rename the label
      rm.update("label", l => `Copy of ${getLabelWithUntitled(l)}`);
      // Store values to update separately
      const tags = rm.get("tags").toJS();
      // Remove unnecessary props
      deleteUnnecessaryProps(rm);
      // Remove the super relationships, will handle later
      deleteSuperRelationships(rm);
      // create actions
      const action = batchOperations.createItemInSequenceWithProps(rm.get("type"), rm, rm.get("sequenceIndex") + 1);
      actions.push(action);
      // Update the ids of the items
      const newId = batchOperations.findCreatedItemId(action);
      rm.set("id", newId);
      // Add tags
      actions.push(getPasteTagsActions(tags, newId));
    });
  });
  // Select all new items
  actions.push(appStateOperations.selectItems(itemsMapsList.map(im => im.get("id")).toJS()));

  // Fix parent/children relationship
  actions = actions.concat(
    filteredItems.reduce((superActions, i, index) => {
      const superId = i.get("super");
      if (superId) {
        const superIndex = filteredItems.findIndex(fi => fi.get("id") === superId);
        superActions.push(
          itemOperations.setSuper(
            [itemsMapsList.get(index).get("id")],
            // Keep the super relationship between the pasting items
            // if they are both copying
            superIndex >= 0 ? itemsMapsList.get(superIndex).get("id") : superId
          )
        );
      }
      return superActions;
    }, [])
  );

  // Fix the relationships (Dependencies, Date constraints, References)
  const { relationshipActions, processedItemsMaps } = processRelsOfPastingItemsInProject(
    filteredItems,
    itemsMapsList,
    itemsById
  );
  actions = actions.concat(relationshipActions);
  return { actions, items: processedItemsMaps };
};

const deleteSuperRelationships = rm => {
  rm.delete("super");
  rm.set("children", List());
};

const checkPropertyValues = (rm, typesById, propertiesById, copiedProperties) => {
  rm.update("propertyValues", pvs =>
    pvs.filter((pValue, pKey) => {
      const itemType = typesById.get(rm.get("type"));
      // The type has the property
      // and the property format matches
      // then keep the property value
      return (
        itemType.properties.includes(pKey) &&
        propertiesById.get(pKey).format === copiedProperties.getIn([pKey, "format"])
      );
    })
  );
};

/**
 * Fix the relationships (Dependencies, Date constraints, References)
 * @param {[Immutable.Map]} origItems
 * @param {List} itemsMaps
 */
const processRelsOfPastingItemBetweenProjects = (origItems, itemsMaps, typesById, referencesById) => {
  let relationshipActions = [];
  let processedItemsMaps = itemsMaps;
  let mappedOrigItems = fromJS(origItems);

  const processRelationship = (thisItem, thisItemIndex, thisKey, otherKey, isThisSubject, createActionFunc) => {
    const otherSide = isThisSubject ? "object" : "subject";
    const thisRelationships = thisItem.get(thisKey);
    thisRelationships.forEach(thisRel => {
      // Check if the other side is in the pasting items
      let otherSideItemIndex = -1;
      const foundOtherSideItem = mappedOrigItems.find((otherI, otherIndex) => {
        otherSideItemIndex = otherIndex;
        const otherId = otherI.get("id");
        return otherId !== thisItem.get("id") && otherId === thisRel.get(otherSide);
      });

      if (foundOtherSideItem) {
        const thisProcessedId = processedItemsMaps.get(thisItemIndex).get("id");
        const otherProcessedId = processedItemsMaps.get(otherSideItemIndex).get("id");

        const newSubId = isThisSubject ? thisProcessedId : otherProcessedId;
        const newObjId = isThisSubject ? otherProcessedId : thisProcessedId;

        // Create new relationship
        const newDepAction = createActionFunc(newSubId, newObjId, thisRel);
        // Push action to actions array
        if (newDepAction) {
          relationshipActions.push(newDepAction);
        }

        // Important: remove the value in the original items in case double processing
        // Remove the value of this item
        mappedOrigItems = mappedOrigItems.updateIn([thisItemIndex, thisKey], rels =>
          rels.filter(rel => rel.get("id") !== thisRel.get("id"))
        );

        // Remove the value of other item
        mappedOrigItems = mappedOrigItems.updateIn([otherSideItemIndex, otherKey], rels =>
          rels.filter(rel => rel.get("id") !== thisRel.get("id"))
        );
      }
    });
  };

  /**
   * A shortcut of calling the processRelationship on the dual relationships attributes
   */
  const processPairRelationships = (origI, oiIndex, thisKey, otherKey, isThisSubject, createActionFunc) => {
    processRelationship(origI, oiIndex, thisKey, otherKey, isThisSubject, createActionFunc);
    processRelationship(origI, oiIndex, otherKey, thisKey, !isThisSubject, createActionFunc);
  };

  mappedOrigItems.forEach((origI, oiIndex) => {
    // Tricky: the items in mappedOrigItems may be changed during processing
    // get the latest item from the mappedOrigItems
    const thisItem = mappedOrigItems.get(oiIndex);

    // Dependencies
    processPairRelationships(
      thisItem,
      oiIndex,
      "blockingDependencies",
      "blockedByDependencies",
      true,
      (subId, objId, oldRel) => {
        return dependencyOperations.createDependencies([subId], [objId], oldRel.get("dependencyType"), [subId, objId]);
      }
    );
    // Relationships
    processPairRelationships(thisItem, oiIndex, "references", "inverseReferences", true, (subId, objId, oldRel) => {
      // If the reference is existed
      // and subject item type has the reference
      // and object item is in the reference types
      const referenceId = oldRel.get("reference");
      const reference = referencesById.get(referenceId);
      if (
        reference &&
        typesById.get(itemsMaps.find(i => i.get("id") === subId).get("type")).references.includes(referenceId) &&
        reference.types.includes(itemsMaps.find(i => i.get("id") === objId).get("type"))
      ) {
        return relationshipOperations.createRelationship([subId], oldRel.get("reference"), [objId]);
      } else if (referencesById.get(REFERENCE_RELATES_TO)) {
        // Fall back to relatesTo
        return relationshipOperations.createRelationship([subId], REFERENCE_RELATES_TO, [objId]);
      }
      return null;
    });
    // Date constraints
    processRelationship(thisItem, oiIndex, "dateConstraints", "dateConstraints", true, (subId, objId, oldRel) => {
      const constraintType = oldRel.get("constraintType");
      if (constraintType === ConstraintTypes.Relative) {
        return dateConstraintOperations.createRelativeConstraints(
          [subId],
          oldRel.get("subjectTarget"),
          objId,
          oldRel.get("objectTarget"),
          oldRel.get("comparison"),
          TimespanRecord(oldRel.get("offset"))
        );
      } else if (constraintType === ConstraintTypes.Date) {
        return dateConstraintOperations.createDateConstraints(
          [subId],
          oldRel.get("subjectTarget"),
          oldRel.get("comparison"),
          oldRel.get("date")
        );
      } else if (constraintType === ConstraintTypes.DatePart) {
        return dateConstraintOperations.createDatePartConstraints(
          [subId],
          oldRel.get("subjectTarget"),
          oldRel.get("partPrecision"),
          oldRel.get("partValue")
        );
      }
    });
  });
  return { relationshipActions, processedItemsMaps };
};

const addLinksBetweenProjects = rm => {
  const actions = [];
  const image = rm.get("image");
  if (image) {
    actions.push(
      batchOperations.createAttachmentAndAssignToItemsImages([rm.get("id")], image.get("label"), image.get("path"))
    );
    rm.delete("image");
  }
  rm.get("links").forEach(l => {
    actions.push(batchOperations.createAttachmentAndAssignToItemsLinks([rm.get("id")], l.get("label"), l.get("path")));
  });
  rm.delete("links");
  rm.get("attachments").forEach(a => {
    actions.push(batchOperations.createAttachmentAndAssignToItemsLinks([rm.get("id")], a.get("label"), a.get("path")));
  });
  rm.delete("attachments");
  return actions;
};

const processPastingBetweenProjects = (
  copiedDataMap,
  canViewPasteItemFunc,
  allowedTypeIds,
  defaultTypeId,
  canCopiedTypeConvertFunc,
  typesById,
  propertiesById,
  colorsById,
  referencesById
) => {
  let actions = [];
  // Filter out the items can NOT paste in the view
  // If type not existed in target project,
  // see if the type can convert to default type
  const convertTypes = copiedDataMap.get("types").reduce((typesMap, t) => {
    if (!allowedTypeIds.includes(t.get("id")) && canCopiedTypeConvertFunc(t.toJS())) {
      typesMap[t.get("id")] = defaultTypeId;
    }
    return typesMap;
  }, {});

  const filteredItems = copiedDataMap.get("items").reduce((filtered, i) => {
    const itemType = i.get("type");
    const convertType = convertTypes[itemType];
    if (convertType) {
      // Set the copied item type to be the convert type
      return filtered.push(i.set("type", convertType));
    } else if (!canViewPasteItemFunc || canViewPasteItemFunc(i)) {
      return filtered.push(i);
    }
    return filtered;
  }, List());

  // Process attributes
  const itemsMapsList = filteredItems.map(i => {
    return i.withMutations(rm => {
      // Convert the flatten value back to Id
      // No need when copying between projects

      // Convert the color
      convertColor(rm, colorsById);
      // Store values to update separately
      const tags = rm.get("tags").toJS();
      // Remove unnecessary props
      deleteUnnecessaryProps(rm);
      deleteSuperRelationships(rm);
      // Proecess property values
      checkPropertyValues(rm, typesById, propertiesById, copiedDataMap.get("properties"));
      // create actions
      const action = batchOperations.createItemInSequenceWithProps(rm.get("type"), rm, rm.get("sequenceIndex") + 1);
      actions.push(action);
      // Update the ids of the items
      const newId = batchOperations.findCreatedItemId(action);
      rm.set("id", newId);
      // Add links/image/attachments
      // and delete them from the rm
      actions = actions.concat(addLinksBetweenProjects(rm));
      // Add tags
      actions.push(getPasteTagsActions(tags, newId));
    });
  });

  // Select all new items
  actions.push(appStateOperations.selectItems(itemsMapsList.map(im => im.get("id")).toJS()));

  // Fix parent/children relationship if there is any between copied items
  actions = actions.concat(
    filteredItems.reduce((superActions, i, index) => {
      const superId = i.get("super");
      if (superId) {
        const superIndex = filteredItems.findIndex(fi => fi.get("id") === superId);
        if (superIndex >= 0) {
          // Use the new Id from the processed itemsMapsList
          superActions.push(
            itemOperations.setSuper([itemsMapsList.get(index).get("id")], itemsMapsList.get(superIndex).get("id"))
          );
        }
      }
      return superActions;
    }, [])
  );

  // Fix the relationships (Dependencies, Date constraints, References)
  const { relationshipActions, processedItemsMaps } = processRelsOfPastingItemBetweenProjects(
    filteredItems,
    itemsMapsList,
    typesById,
    referencesById
  );
  actions = actions.concat(relationshipActions);
  return { actions, items: processedItemsMaps };
};

const processPasting = (
  copiedDataMap,
  projectId,
  isActiveView,
  canViewPasteItemFunc,
  allowedTypeIds,
  defaultTypeId,
  canCopiedTypeConvertFunc,
  typesById,
  propertiesById,
  colorsById,
  referencesById,
  itemsById
) => {
  if (isCopiedDataFormSameProject(copiedDataMap.get("projectId"), projectId)) {
    return processPastingInProject(copiedDataMap, canViewPasteItemFunc, colorsById, itemsById);
  } else {
    return processPastingBetweenProjects(
      copiedDataMap,
      canViewPasteItemFunc,
      allowedTypeIds,
      defaultTypeId,
      canCopiedTypeConvertFunc,
      typesById,
      propertiesById,
      colorsById,
      referencesById
    );
  }
};

const isPastingItems = e => {
  const clipboardData = e.clipboardData || window.clipboardData;
  const availableTypes = clipboardData.types;
  const plainData = clipboardData.getData(pasteDataTypes.PLAINTEXT);
  return (
    availableTypes.indexOf(pasteDataTypes.AEON_ITEMS) >= 0 ||
    (plainData && plainData.startsWith(pasteDataTypes.AEON_ITEMS))
  );
};

const getCleanedCopiedData = e => {
  const clipboardData = e.clipboardData || window.clipboardData;
  const availableTypes = clipboardData.types;
  let copiedData = "";
  if (availableTypes.indexOf(pasteDataTypes.AEON_ITEMS) >= 0) {
    copiedData = clipboardData.getData(pasteDataTypes.AEON_ITEMS);
  } else if (availableTypes.indexOf(pasteDataTypes.PLAINTEXT) >= 0) {
    copiedData = clipboardData.getData(pasteDataTypes.PLAINTEXT);
  }
  return copiedData && copiedData.startsWith(pasteDataTypes.AEON_ITEMS)
    ? copiedData.slice(pasteDataTypes.AEON_ITEMS.length)
    : copiedData;
};

const pasteItemsDataFromClipboard = (
  e,
  projectId,
  isActiveView,
  canViewPasteItemFunc,
  allowedTypeIds,
  defaultTypeId,
  canCopiedTypeConvertFunc,
  typesById,
  propertiesById,
  colorsById,
  referencesById,
  itemsById
) => {
  // Pasted data may not be valid
  const cleanedCopiedData = getCleanedCopiedData(e);
  if (cleanedCopiedData) {
    try {
      const copiedDataMap = fromJS(JSON.parse(cleanedCopiedData));
      // If the copied data is parsed successfully, stop the
      // the paste event default action and propagation
      if (e && e.preventDefault && e.stopPropagation) {
        e.preventDefault();
        e.stopPropagation();
      }
      const { actions, items } = processPasting(
        copiedDataMap,
        projectId,
        isActiveView,
        canViewPasteItemFunc,
        allowedTypeIds,
        defaultTypeId,
        canCopiedTypeConvertFunc,
        typesById,
        propertiesById,
        colorsById,
        referencesById,
        itemsById
      );

      return ItemPasteResultRecord({
        success: true,
        actions,
        items
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      return ItemPasteResultRecord({
        success: false,
        errorMessage: interfaceMessages.COPY_AND_PASTE_ITEMS_INVALID_COPIED_DATA
      });
    }
  }

  return ItemPasteResultRecord({
    success: false,
    errorMessage: null
  });
};

/**
 * Entry of pasting items
 * Used in the views
 *
 * @param {object} e
 * @param {string} projectId
 * @param {bool} isActiveView
 * @param {function} canViewPasteItemFunc Can paste item on the rules of the view
 * @param {function} viewExtraProcessFunc processFuncOfView(itemMapList)
 * @param {*} viewExtraProcessFunc
 * @param {*} allowedTypeIds
 * @param {*} defaultTypeId
 * @param {*} canCopiedTypeConvertFunc
 * @param {*} typesById
 * @param {*} propertiesById
 * @param {*} colorsById
 * @param {*} referencesById
 */
const onPasteItems = (
  e,
  projectId,
  isActiveView,
  canViewPasteItemFunc,
  viewExtraProcessFunc,
  allowedTypeIds,
  defaultTypeId,
  canCopiedTypeConvertFunc,
  typesById,
  propertiesById,
  colorsById,
  referencesById,
  itemsById
) => {
  let actions = [];
  if (canPaste(isActiveView)) {
    const result = pasteItemsDataFromClipboard(
      e,
      projectId,
      isActiveView,
      canViewPasteItemFunc,
      allowedTypeIds,
      defaultTypeId,
      canCopiedTypeConvertFunc,
      typesById,
      propertiesById,
      colorsById,
      referencesById,
      itemsById
    );
    // If the copy failed, show an error message if necessary and exit
    if (!result.success) {
      if (result.errorMessage) {
        dialogs.alertPrompt(result.errorMessage);
      }
    } else {
      actions = result.actions;
      if (viewExtraProcessFunc) {
        const extraActions = viewExtraProcessFunc(result.items);
        actions = actions.concat(extraActions);
      }
    }
  }
  return actions;
};

export { getCopyItemsJsonString, getCopyItemsPlainText, isPastingItems,onPasteItems };
